import { useQuery } from '@tanstack/react-query';
import * as Effect from 'effect/Effect';
import { pipe } from 'effect/Function';
import { CategoryService } from '@/effects/categories/category.service';
import { MainLayer } from '@/effects/layers';
import { runEffect } from '@/effects/runtime';
import { QUERY_KEYS } from '@/lib/constants';

export function useGroupCategories(groupId: string) {
  return useQuery({
    queryKey: [QUERY_KEYS.GROUP_CATEGORIES, groupId],
    queryFn: async () => {
      const program = pipe(
        CategoryService,
        Effect.flatMap((service) => service.fetchForGroup(groupId)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    enabled: !!groupId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useCategorySummary(categoryId: string) {
  return useQuery({
    queryKey: [QUERY_KEYS.CATEGORY_SUMMARY, categoryId],
    queryFn: async () => {
      const program = pipe(
        CategoryService,
        Effect.flatMap((service) => service.fetchSummary(categoryId)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    enabled: !!categoryId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useCategorySplits(categoryId: string) {
  return useQuery({
    queryKey: [QUERY_KEYS.CATEGORY_SPLITS, categoryId],
    queryFn: async () => {
      const program = pipe(
        CategoryService,
        Effect.flatMap((service) => service.fetchSplits(categoryId)),
        Effect.provide(MainLayer)
      );

      return runEffect(program);
    },
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to get a specific category
export function useCategory(groupId: string, categoryId: string) {
  const { data: categories, ...rest } = useGroupCategories(groupId);

  return {
    ...rest,
    data: categories?.find((category) => category.id === categoryId),
  };
}

// Hook to get pinned categories for a group
export function usePinnedCategories(
  groupId: string,
  pinnedCategoryIds?: string[] | null
) {
  const { data: categories, ...rest } = useGroupCategories(groupId);

  const pinnedCategories = pinnedCategoryIds
    ? categories?.filter((category) =>
        pinnedCategoryIds.includes(category.id)
      )
    : [];

  return {
    ...rest,
    data: pinnedCategories,
  };
}

// Hook to get categories with their summaries
export function useCategoriesWithSummaries(categoryIds: string[]) {
  const summaries = categoryIds.map((id) => useCategorySummary(id));

  const isLoading = summaries.some((summary) => summary.isLoading);
  const error = summaries.find((summary) => summary.error)?.error;

  return {
    data: summaries.map((summary) => summary.data).filter(Boolean),
    isLoading,
    error,
  };
}